import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Button,
} from '@mui/material'
import { monthNames, years, getInvoiceRange } from '@utils'
import Brightness2Icon from '@mui/icons-material/Brightness2'
import CircleIcon from '@mui/icons-material/Circle'
import NightlightIcon from '@mui/icons-material/Nightlight'
import { Fragment, useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import Settings from '@mui/icons-material/Settings'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon'

type Props = {
    fromDate: DateTime<boolean> | null | undefined
    setFromDate: React.Dispatch<
        React.SetStateAction<DateTime<boolean> | null | undefined>
    >
    toDate: DateTime<boolean> | null | undefined
    setToDate: React.Dispatch<
        React.SetStateAction<DateTime<boolean> | null | undefined>
    >
    monthOnly?: boolean
}

export default function PeriodDatePicker({
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    monthOnly,
}: Props) {
    const [advanced, setAdvanced] = useState<boolean>(false)

    const [month, setMonth] = useState<string>(
        monthNames[fromDate ? fromDate.month - 1 : new Date().getMonth()]
    )
    const [year, setYear] = useState<number>(
        fromDate ? fromDate.year : new Date().getFullYear()
    )
    const [period, setPeriod] = useState<'full' | 'first' | 'second'>('full')

    useEffect(() => {
        const { start_date, end_date } =
            period === 'full'
                ? getInvoiceRange('full-month', month, undefined, year)
                : getInvoiceRange('half-month', month, period, year)
        setFromDate(start_date)
        setToDate(end_date)
    }, [month, year, period, advanced])

    return (
        <Box style={{ marginTop: 32 }}>
            <Box
                style={{
                    textAlign: 'right',
                    marginBottom: '0.5em',
                    gap: 16,
                    width: 500,
                }}
            >
                <Button
                    variant={advanced ? 'contained' : 'outlined'}
                    onClick={() => setAdvanced(!advanced)}
                    startIcon={<Settings />}
                >
                    Advanced
                </Button>
            </Box>
            {advanced ? (
                <Box style={{ display: 'flex', gap: 16, width: 500 }}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DateTimePicker
                            label="From Date"
                            onChange={setFromDate}
                            value={fromDate}
                            timeSteps={{
                                minutes: 1,
                                seconds: 1,
                            }}
                            ampm={false}
                        />
                        <DateTimePicker
                            label="To Date"
                            onChange={setToDate}
                            value={toDate}
                            timeSteps={{
                                minutes: 1,
                                seconds: 1,
                            }}
                            ampm={false}
                            minDateTime={fromDate ?? undefined}
                        />
                    </LocalizationProvider>
                </Box>
            ) : (
                <Fragment>
                    <Box
                        style={{
                            display: 'flex',
                        }}
                    >
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 16,
                                width: 500,
                            }}
                        >
                            <FormControl fullWidth>
                                <InputLabel id="monthSelector">
                                    Month
                                </InputLabel>
                                <Select
                                    labelId="monthSelector"
                                    label="Month"
                                    defaultValue={month}
                                    onChange={(e) => setMonth(e.target.value)}
                                >
                                    {monthNames.map((month, index) => (
                                        <MenuItem key={index} value={month}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="yearSelector">Year</InputLabel>
                                <Select
                                    labelId="yearSelector"
                                    label="Year"
                                    defaultValue={year}
                                    onChange={(e) => {
                                        if (typeof e.target.value === 'string')
                                            return
                                        setYear(e.target.value)
                                    }}
                                >
                                    {years.map((year, index) => (
                                        <MenuItem key={index} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box
                        style={{
                            display: 'flex',
                            marginTop: 32,
                        }}
                    >
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 16,
                                width: 500,
                            }}
                        >
                            <FormControl fullWidth>
                                <ToggleButtonGroup
                                    value={period}
                                    exclusive
                                    onChange={(_, v) => {
                                        if (v !== null) {
                                            setPeriod(v)
                                        }
                                    }}
                                    aria-label="period selection"
                                    color="primary"
                                >
                                    <ToggleButton
                                        value="full"
                                        aria-label="full month"
                                        sx={{
                                            width: '100%',
                                        }}
                                    >
                                        <CircleIcon />
                                        <Typography variant="body1">
                                            Full Month
                                        </Typography>
                                    </ToggleButton>
                                    <ToggleButton
                                        value="first"
                                        aria-label="first half"
                                        sx={{
                                            width: '100%',
                                        }}
                                        disabled={monthOnly}
                                    >
                                        <NightlightIcon />
                                        <Typography variant="body1">
                                            First Half
                                        </Typography>
                                    </ToggleButton>
                                    <ToggleButton
                                        value="second"
                                        aria-label="second half"
                                        sx={{
                                            width: '100%',
                                        }}
                                        disabled={monthOnly}
                                    >
                                        <Brightness2Icon />
                                        <Typography variant="body1">
                                            Second Half
                                        </Typography>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </FormControl>
                        </Box>
                    </Box>
                </Fragment>
            )}
        </Box>
    )
}
